import React, { useCallback, useMemo } from "react";
import styles from "./TabLayout.module.scss";
import { UsageEnum, UsageUpgradeEnum } from "@types";
import CalendarIcon from "src/assets/icons/calendar.svg?react";
import ChevronDown from "src/assets/icons/dropdown.svg?react";
import RewardIcon from "src/assets/icons/grade_reward.svg?react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Dropdown } from "antd";
import moment from "moment";
import type { MenuProps } from "antd";
import { SelectOption } from "@src/types";
import { hasArrayValues } from "@src/utils/helpers";
import UpgradeInProgressIcon from "src/assets/icons/upgrade_inprogress_icon.svg?react";

type Props = {
  usageMode: string;
  handleSelectUsageMode?: (usageMode: any) => void;
  handleFilterClick?: () => void;
  filters?: any[];
  showFilterMenu?: boolean;
  startDate: Date | undefined;
  endDate: Date | undefined;
  dateRange: any[];
  showDatePicker: boolean;
  setShowDatePicker: (value: boolean) => void;
  onDateChange: (dates: any[]) => void;
  selSchool?: SelectOption | undefined;
  schoolOptions?: SelectOption[];
  handleSelectSchool?: (school: SelectOption) => void;
  showSchoolMenu: boolean;
  setShowSchoolMenu: (value: boolean) => void;
};

function TabLayout(props: Props) {
  const {
    usageMode,
    showFilterMenu,
    showDatePicker,
    startDate,
    endDate,
    selSchool,
    schoolOptions = [],
    handleSelectSchool,
    showSchoolMenu,
    setShowSchoolMenu,
    setShowDatePicker,
    handleSelectUsageMode,
    onDateChange,
  } = props;

  const _filtered = useMemo(() => {
    return (Object.keys(UsageEnum) as (keyof typeof UsageEnum)[]).map((key) => {
      return UsageEnum[key];
    });
  }, []);

  const handleDatePickerToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (showDatePicker) {
      setShowDatePicker(false);
    }
  };

  const DateCustomInput = (
    <div className={styles.selectSchoolView}>
      <div className={styles.schoolField}>
        <div className={styles.durationLabel}>
          <CalendarIcon width={28} height={28} />
          <div className={styles.dateLabel}>
            From <span>{moment(startDate).format("YYYY-MM-DD")}</span> → Date{" "}
            <span>{moment(endDate).format("YYYY-MM-DD")}</span>
          </div>
        </div>
      </div>
      <div
        className={[
          styles.dropdownToggle,
          showDatePicker
            ? styles.dropdownToggleClose
            : styles.dropdownToggleOpen,
        ].join(" ")}
        onClick={handleDatePickerToggle}
      >
        <ChevronDown className={styles.icon} />
      </div>
    </div>
  );

  const schoolMenu = useMemo(() => {
    // District Admin
    const items: MenuProps["items"] = (schoolOptions || [])
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((gradeLevel, index) => {
        return {
          key: index,
          label: (
            <div
              className={`${styles.gradeDropDownMenuitem} ${selSchool?.label === gradeLevel.label ? styles.select : ""}`}
            >
              {gradeLevel.label}
            </div>
          ),
        };
      });
    return items;
  }, [selSchool, schoolOptions]);

  const selectSchool: MenuProps["onClick"] = ({ key }) => {
    setShowSchoolMenu(false);
    handleSelectSchool?.(schoolOptions[key]);
  };

  const handleSchoolDropdownVisible = useCallback(
    (visible: boolean) => {
      setShowSchoolMenu(visible);
    },
    [setShowSchoolMenu],
  );

  const handleChange = (dates: any) => {
    onDateChange(dates);
    setShowDatePicker(false);
  };

  const toggleDatePicker = () => {
    setShowDatePicker(true);
  };

  return (
    <div className={styles.wrapper}>
      <div
        className={styles.navView}
        style={{ opacity: showFilterMenu ? "0" : "1" }}
      >
        <div className={styles.tabLayout}>
          {_filtered.map((item, index) => {
            const upgradeEnumKey = Object.keys(UsageUpgradeEnum).find(
              (key) =>
                UsageUpgradeEnum[key as keyof typeof UsageUpgradeEnum] ===
                "true",
            );

            if (upgradeEnumKey === item) {
              return (
                <div
                  key={index}
                  className={styles.itemWrapper}
                >
                  <div
                    className={[
                      styles.upgradeListItem,
                      usageMode === item ? styles.selItem : "",
                    ].join(" ")}
                  >
                    <div
                      className={[
                        styles.upgradeListTitle,
                        usageMode === item ? styles.selItemTitle : "",
                      ].join(" ")}
                    >
                      {item}
                    </div>
                  </div>
                  <div className={styles.upgradeButton}>
                    <UpgradeInProgressIcon />
                    <span className={styles.upgradeButtonText}>Upgrade in progress</span>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={index}
                className={styles.itemWrapper}
                onClick={() => handleSelectUsageMode?.(item)}
              >
                <div
                  className={[
                    styles.listItem,
                    usageMode === item ? styles.selItem : "",
                  ].join(" ")}
                >
                  <div
                    className={[
                      styles.listTitle,
                      usageMode === item ? styles.selItemTitle : "",
                    ].join(" ")}
                  >
                    {item}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles.filterView}>
        <div className={styles.subView}>
          <div>
            {hasArrayValues(schoolOptions) && schoolOptions.length > 2 && (
              <Dropdown
                menu={{ items: schoolMenu, onClick: selectSchool }}
                trigger={["click"]}
                placement="bottom"
                onOpenChange={handleSchoolDropdownVisible}
                open={showSchoolMenu}
              >
                <div className={styles.selectSchoolView}>
                  <div className={styles.schoolField}>
                    <div className={styles.schoolLabel}>
                      <RewardIcon width={30} />
                      <span>{selSchool?.label}</span>
                    </div>
                  </div>
                  <div
                    className={[
                      styles.dropdownToggle,
                      showSchoolMenu
                        ? styles.dropdownToggleClose
                        : styles.dropdownToggleOpen,
                    ].join(" ")}
                  >
                    <ChevronDown className={styles.icon} />
                  </div>
                </div>
              </Dropdown>
            )}
          </div>
          {
            <div>
              <DatePicker
                selectsRange
                startDate={startDate}
                endDate={endDate}
                selected={endDate}
                onChange={handleChange}
                onClickOutside={() => setShowDatePicker(false)}
                customInput={DateCustomInput}
                onCalendarOpen={toggleDatePicker}
                maxDate={new Date()}
              />
            </div>
          }
        </div>
      </div>
    </div>
  );
}

export default TabLayout;
